import React, { useState, useEffect } from "react";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  Grid,
  InputAdornment,
  IconButton,
  TextField,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography
} from "@mui/material";
import {
  CognitoIdentityProviderClient,
  SignUpCommand,
} from "@aws-sdk/client-cognito-identity-provider";

const poolData = {
  ClientId: process.env.REACT_APP_USER_POOL_CLIENT_ID,
  UserPoolId: process.env.REACT_APP_USER_POOL_ID,
};

const cognitoClient = new CognitoIdentityProviderClient({
  region: process.env.REACT_APP_AWS_REGION,
});

export default function UserSignUp({ onSignUpComplete, setShowSignUp }) {
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [authDialogMessage, setAuthDialogMessage] = useState("");
  const [openAuthDialog, setOpenAuthDialog] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showValidations, setShowValidations] = useState(false);
    const [passwordValidations, setPasswordValidations] = useState({
      hasNumber: false,
      hasSpecialChar: false,
      hasUpperCase: false,
      hasLowerCase: false,
    });

 useEffect(() => {
     if (password !== confirmPassword) {
       setError("New Password and Confirm Password do not match.");
       return;
     } else {
       setError("");
     }
   }, [confirmPassword]);

  const handleSignUp = async () => {
    if (password !== confirmPassword) {
      setError("Password and Confirm Password do not match");
      return;
    }
    if (!username || !password || !email) {
      alert("Username, password, and email are required.");
      return;
    }

    setIsLoading(true);
    const signUpParams = {
      ClientId: poolData.ClientId,
      Username: username,
      Password: password,
      UserAttributes: [{ Name: "email", Value: email }],
    };

    try {
      const command = new SignUpCommand(signUpParams);
      const result = await cognitoClient.send(command);

      if (!result.UserConfirmed) {
        setAuthDialogMessage(
          "Sign up successful! Your account is pending admin confirmation."
        );
        setOpenAuthDialog(true);
        return;
      }
      onSignUpComplete(username, password);
    } catch (err) {
      console.error("Sign up failed:", err);
      alert("Sign up failed: " + (err.message || "Unknown error"));
    } finally {
      setIsLoading(false);
    }
  };
  const validatePassword = (password) => {
    setPassword(password);
    setPasswordValidations({
      hasNumber: /\d/.test(password),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
      hasUpperCase: /[A-Z]/.test(password),
      hasLowerCase: /[a-z]/.test(password),
    });
  };
  const getValidationIcon = (isValid) => (isValid ? "✅" : "❌");

  return (
    <Grid container spacing={0}>
      <Grid item sm={12} md={3} lg={4} />
      <Grid item sm={12} md={6} lg={4}>
        <div className="signupCard">
          <h1
            className="gradientText"
            style={{
              fontWeight: 900,
              fontSize: "36px",
              lineHeight: 1.2,
              textTransform: "capitalize",
              margin: 0,
            }}
          >
            Create Your Account
          </h1>
          <Box
            component="form"
            sx={{ "& .MuiTextField-root": { my: 1 } }}
            noValidate
            autoComplete="off"
          >
            <TextField
              fullWidth
              required
              label="Username"
              variant="standard"
              onChange={(e) => setUsername(e.target.value)}
            />
            <TextField
              fullWidth
              required
              label="Email"
              type="email"
              variant="standard"
              onChange={(e) => setEmail(e.target.value)}
            />
            <TextField
              fullWidth
              required
              label="Password"
              type={showPassword ? "text" : "password"}
              variant="standard"
              onFocus={() => setShowValidations(true)}
              onBlur={() => setShowValidations(false)}
              onChange={(e) => validatePassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {showValidations && <Box textAlign="left" mt={1}>
              <Typography variant="caption" color={passwordValidations.hasNumber ? "rgb(23 203 43)" : "red"}>
                {getValidationIcon(passwordValidations.hasNumber)} Contains at least 1 number
              </Typography>
              <br />
              <Typography variant="caption" color={passwordValidations.hasSpecialChar ? "rgb(23 203 43)" : "red"}>
                {getValidationIcon(passwordValidations.hasSpecialChar)} Contains at least 1 special character
              </Typography>
              <br />
              <Typography variant="caption" color={passwordValidations.hasUpperCase ? "rgb(23 203 43)" : "red"}>
                {getValidationIcon(passwordValidations.hasUpperCase)} Contains at least 1 uppercase letter
              </Typography>
              <br />
              <Typography variant="caption" color={passwordValidations.hasLowerCase ? "rgb(23 203 43)" : "red"}>
                {getValidationIcon(passwordValidations.hasLowerCase)} Contains at least 1 lowercase letter
              </Typography>
            </Box>}
            <TextField
              fullWidth
              required
              label="Confirm Password"
              type={showPassword ? "text" : "password"}
              variant="standard"
              onChange={(e) => setConfirmPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment
                    position="start"
                    style={{ marginTop: -24, marginRight: 0 }}
                  >
                    <IconButton onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {error && <p style={{ color: "red" }}>{error}</p>}
            <Box display="flex" justifyContent="flex-end">
              <Button variant="contained" color="primary" onClick={handleSignUp} style={{ margin: "16px 0 16px 0" }} disabled={isLoading}>
                Sign Up
              </Button>
            </Box>
            <Button
              fullWidth
              variant="text"
              color="secondary"
              // onClick={onSignUpComplete()}
              onClick={onSignUpComplete}
            >
              Already have an account? Login
            </Button>
          </Box>
        </div>
      </Grid>
      <Grid item sm={12} md={3} lg={4} />

      {/* Dialog for admin confirmation */}
      <Dialog
        open={openAuthDialog}
        onClose={() => {
          setOpenAuthDialog(false);
          setShowSignUp(false);
        }}
      >
        <DialogTitle style={{ backgroundColor: "#455A64", color: "white" }}>
          Account Status
        </DialogTitle>
        <DialogContent style={{ backgroundColor: "rgba(0, 0, 0, 0.05)" }}>
          <DialogContentText>{authDialogMessage}</DialogContentText>
        </DialogContent>
        <DialogActions style={{ backgroundColor: "rgba(0, 0, 0, 0.05)" }}>
          <Button
            onClick={() => {
              setOpenAuthDialog(false);
              setShowSignUp(false);
            }}
            color="primary"
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}
